import { useState } from 'react'

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import PlanningConflict from '@components/PlanningConflict'
import type { Entity } from '@models/Entity'
import type { GroupedOption } from '@models/GroupedOption'
import { formatDateToISOWeek } from '@util/date-util'
import { activiteittypes as hoofdleidingActiviteitTypes } from '@util/hoofdleiding-util'

import { ENTITIES } from '../../..'
import type { Project } from '../../../../aansluiting/project/types/Project'
import { SelectObject } from '../../../../core/components/SelectObject'
import { SelectString } from '../../../../core/components/SelectString'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import { StyledError } from '../../../../theme/components/StyledError'
import type { Activiteit } from '../../types/Activiteit'

export { CreateEditActiviteitForm }
export type { ActiviteitFormValues }

const activiteitTypes = [
  'Afspraak',
  'Vergunning aanvragen',
  'Bodemonderzoek aanvragen',
  'Hoofdleiding aanleggen',
  'Extra werkvoorbereiding',
  'Vestiging zakelijk recht',
  'Wacht op klant',
  'Klantcontact',
  'Netcongestie Elektra',
  'Voorcalculatie aanleveren',
]

const getActivityTypesByEntity = ({ type, projecttype }: Entity) => {
  if (type === ENTITIES.PROJECT && projecttype === 'Hoofdleiding')
    return hoofdleidingActiviteitTypes

  return activiteitTypes
}

const createOptionsForEdit = (
  assignees: GroupedOption[],
  assignedTo: string[],
) => {
  const toReturn: any[] = []

  assignees.forEach((x) => {
    if (assignedTo.includes(x.label)) toReturn.push(x)
  })

  return toReturn
}

type ActiviteitFormValues = {
  type: string
  assignedTo: string[]
  text: string
  dueDate: Date | null
}

type Props = {
  activiteit?: Activiteit
  isEdit: boolean
  assignees: GroupedOption[]
  entity: Entity
  entityProps?: Partial<Project>
  geblokkeerdeWeken: GeblokkeerdeWeek[]
  error: unknown
  progress: { current: number; total: number; error: boolean }
  onSubmit: (values: ActiviteitFormValues) => void
  onCancel: () => void
}

const CreateEditActiviteitForm = ({
  activiteit,
  isEdit,
  entity,
  assignees,
  entityProps,
  geblokkeerdeWeken,
  error,
  progress,
  onSubmit,
  onCancel,
}: Props) => {
  const editedActiviteit = isEdit ? activiteit : undefined

  const [assignedTo, setAssignedTo] = useState<GroupedOption[]>(() =>
    editedActiviteit
      ? createOptionsForEdit(assignees, editedActiviteit.assignedTo)
      : [],
  )
  const [text, setText] = useState(editedActiviteit?.text ?? '')
  const [errors, setErrors] = useState({})
  const [dueDate, setDueDate] = useState<Date | null>(
    editedActiviteit?.dueDate ? new Date(editedActiviteit.dueDate) : null,
  )
  const [type, setType] = useState<string>(editedActiviteit?.type ?? 'Afspraak')
  const activiteitLabel = `activiteit${assignedTo.length === 1 ? '' : 'en'}`

  // Only possible when creating; every type starts with an empty form
  const handleTypeChange = (type: string) => {
    setType(type)
    setText('')
    setAssignedTo([])
    setDueDate(null)
    setErrors({})
  }

  const validate = () => {
    const textError = !text || text === ''
    const typeError = !type
    const assignedToError =
      type !== 'Afspraak' && type !== 'Klantcontact' && assignedTo.length === 0
    const dueDateError =
      !['Afspraak', 'Klantcontact', 'Netcongestie Elektra'].includes(type) &&
      !dueDate

    setErrors({
      textError,
      typeError,
      assignedToError,
      dueDateError,
    })

    return !textError && !typeError && !assignedToError && !dueDateError
  }

  const handleClickOpslaan = () => {
    if (!validate()) return

    onSubmit({
      type,
      assignedTo: assignedTo.map(({ label }) => label),
      text,
      dueDate,
    })
  }

  return (
    <>
      <DialogTitle>
        {isEdit ? 'Bewerken activiteit' : 'Aanmaken activiteit'}
      </DialogTitle>

      <DialogContent>
        {type === 'Extra werkvoorbereiding' &&
          dueDate !== null &&
          !!entityProps &&
          !!entityProps.geplandeUitvoeringsdatum &&
          !!entityProps.geplandeUitvoeringsdatumType &&
          !!entityProps.combi && (
            <PlanningConflict
              geplandeUitvoeringsdatum={entityProps.geplandeUitvoeringsdatum}
              geplandeUitvoeringsdatumType={
                entityProps.geplandeUitvoeringsdatumType
              }
              opleverdatum={dueDate}
              combi={entityProps.combi}
            />
          )}

        <StyledError error={error} />

        <DialogContentText>
          {isEdit
            ? 'Bewerk een activiteit'
            : 'Kies een type activiteit om aan te maken en wijs hem optioneel toe aan een organisatie'}
        </DialogContentText>

        <GeblokkeerdeWekenWarning
          geblokkeerdeWeken={geblokkeerdeWeken}
          isoWeek={formatDateToISOWeek(dueDate)}
          gebiedscode={
            entity.gebiedscode ||
            /* v8 ignore next - TODO: missing coverage -- @preserve */ ''
          }
        />

        <form>
          <FormControl sx={{ mt: 1.5, width: '100%' }}>
            <SelectString
              options={getActivityTypesByEntity(entity)}
              onChange={handleTypeChange}
              disableClearable
              value={type}
              label="Type"
              error={errors['typeError']}
              disabled={isEdit}
            />
          </FormControl>

          <FormControl
            sx={{
              mt: 1.5,
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <SelectObject
              multiple
              options={assignees}
              onChange={setAssignedTo}
              value={assignedTo}
              label="Toegewezen aan"
              error={errors['assignedToError']}
              sx={{ width: '50%', pr: 1, marginTop: 0.4 }}
              disabled={isEdit || type === 'Klantcontact'}
            />

            <DatePicker
              label="Opleverdatum"
              disablePast
              value={dueDate}
              onChange={setDueDate}
              displayWeekNumber
              disabled={type === 'Klantcontact'}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { width: '50%' },
                  error: errors['dueDateError'],
                },
              }}
            />
          </FormControl>

          <FormControl sx={{ mt: 3, width: '100%' }}>
            <TextField
              value={text}
              multiline
              rows={6}
              label={`Opmerking (${text.length}/500)`}
              onChange={(e) => setText(e.target.value)}
              required
              error={errors['textError']}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </FormControl>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary" autoFocus>
          Annuleren
        </Button>

        {isEdit && (
          <Button onClick={handleClickOpslaan} color="primary">
            Bewerken
          </Button>
        )}

        {!isEdit && progress.error && (
          <Button onClick={handleClickOpslaan} color="primary">
            Opnieuw proberen (vanaf {progress.current}e activiteit)
          </Button>
        )}

        {!isEdit && !progress.error && progress.total > progress.current && (
          <>
            <Button disabled>
              Aanmaken {activiteitLabel}... ({progress.current}/{progress.total}
              )
            </Button>
          </>
        )}

        {!isEdit && progress.current === 0 && (
          <Button onClick={handleClickOpslaan} color="primary">
            Aanmaken
            {assignedTo.length > 0 &&
              ` (${assignedTo.length} ${activiteitLabel})`}
          </Button>
        )}
      </DialogActions>
    </>
  )
}
